/**
 * Project Task Detail Modal
 *
 * Modal displaying full details of a project task with sections for:
 * - Task information (title, description, assignee, status)
 * - Todo items list
 * - Action notes
 *
 * Similar to bug-report-detail.tsx but in modal format
 */

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ListChecks,
  UserCircle,
  PencilSimple,
  X,
  CalendarIcon,
  LockKey,
} from "@phosphor-icons/react";
import { ProjectTask } from "shared/types/renovation";
import { useUsers } from "shared/contexts/users.context";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { TodoListSection } from "shared/components/TodoListSection";
import { ProjectTaskActionNotesSection } from "./ProjectTaskActionNotesSection";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { BlockTaskModal } from "./BlockTaskModal";

interface ProjectTaskDetailModalProps {
  task: ProjectTask | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const ProjectTaskDetailModal: React.FC<ProjectTaskDetailModalProps> = ({
  task: initialTask,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const { session } = useSession();
  const { users } = useUsers();
  const [task, setTask] = useState<ProjectTask | null>(initialTask);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [isAssigneePickerOpen, setIsAssigneePickerOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isEditingPhase, setIsEditingPhase] = useState(false);

  // Update local task when prop changes
  useEffect(() => {
    setTask(initialTask);
    if (initialTask) {
      setEditedTitle(initialTask.title);
      setEditedDescription(initialTask.description || "");
    }
  }, [initialTask]);

  const getUserName = (userId: string) => {
    const foundUser = users.find((u) => u.id === userId);
    if (foundUser) {
      const firstName = foundUser.firstName || foundUser.first_name || "";
      const lastName = foundUser.lastName || foundUser.last_name || "";
      return `${firstName} ${lastName}`.trim() || "Onbekende gebruiker";
    }
    return "Onbekende gebruiker";
  };

  const getUserImageUrl = (userId: string) => {
    return `https://img.clerk.com/${userId}`;
  };

  const handleUpdateTitle = async () => {
    if (!task || !editedTitle.trim()) {
      toast.error("Titel kan niet leeg zijn");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: editedTitle }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update title");
      }

      const updatedTask = await response.json();
      setTask(updatedTask);
      setIsEditingTitle(false);
      toast.success("Titel bijgewerkt");
      onUpdate();
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error("Fout bij bijwerken titel");
    }
  };

  const handleUpdateDescription = async () => {
    if (!task) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description: editedDescription }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update description");
      }

      const updatedTask = await response.json();
      setTask(updatedTask);
      setIsEditingDescription(false);
      toast.success("Omschrijving bijgewerkt");
      onUpdate();
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error("Fout bij bijwerken omschrijving");
    }
  };

  const handleUpdateAssignee = async (user: any) => {
    if (!task?.id) {
      console.error("Task ID is required");
      toast.error("Fout bij bijwerken toewijzing");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignedTo: user?.id || null }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update assignee");
      }

      const updatedTask = await response.json();
      setTask(updatedTask);
      toast.success("Toewijzing bijgewerkt");
      onUpdate();
    } catch (error) {
      console.error("Error updating assignee:", error);
      toast.error("Fout bij bijwerken toewijzing");
    }
  };

  const handleUpdatePhase = async (phase: string) => {
    if (!task?.id) {
      console.error("Task ID is required");
      toast.error("Fout bij bijwerken projectfase");
      return;
    }

    const newPhase = phase === "NONE" ? null : phase;
    const oldPhase = task.projectPhase;

    // OPTIMISTIC UPDATE: Update UI immediately
    setTask({ ...task, projectPhase: newPhase as any });
    setIsEditingPhase(false);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${task.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectPhase: newPhase }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update phase");
      }

      const updatedTask = await response.json();
      setTask(updatedTask);
      toast.success("Projectfase bijgewerkt");
      onUpdate(); // Refresh the kanban board
    } catch (error) {
      console.error("Error updating phase:", error);
      toast.error("Fout bij bijwerken projectfase");

      // ROLLBACK: Revert to old phase on error
      setTask({ ...task, projectPhase: oldPhase });
    }
  };

  if (!task) return null;

  const timeAgo = formatDistanceToNow(new Date(task.createdAt), {
    addSuffix: true,
    locale: nl,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto p-0">
        {/* Header */}
        <div className="border-neutral-primary/10 sticky top-0 z-10 border-b bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <ListChecks
                  size={24}
                  className="text-moss-primary"
                  weight="duotone"
                />
                <Badge className="bg-moss-primary/10 text-moss-primary border-0">
                  TASK-{task.id}
                </Badge>
                <Badge variant="outline" className="border-neutral-300">
                  {task.status}
                </Badge>
              </div>

              {/* Editable Title */}
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-lg font-semibold"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdateTitle();
                      if (e.key === "Escape") {
                        setIsEditingTitle(false);
                        setEditedTitle(task.title);
                      }
                    }}
                  />
                  <Button size="sm" onClick={handleUpdateTitle}>
                    Opslaan
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditingTitle(false);
                      setEditedTitle(task.title);
                    }}
                  >
                    Annuleren
                  </Button>
                </div>
              ) : (
                <div
                  className="group -ml-2 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-neutral-50"
                  onClick={() => setIsEditingTitle(true)}
                >
                  <h2 className="text-neutral-primary text-lg font-semibold">
                    {task.title}
                  </h2>
                  <PencilSimple
                    size={16}
                    className="text-moss-primary opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </div>
              )}

              <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
                <CalendarIcon size={14} />
                <span>Aangemaakt {timeAgo}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={task.isBlocked ? "default" : "outline"}
                size="sm"
                onClick={() => setIsBlockModalOpen(true)}
                className={
                  task.isBlocked
                    ? "bg-amber-600 text-white hover:bg-amber-700"
                    : ""
                }
              >
                <LockKey size={16} className="mr-2" />
                {task.isBlocked ? "Geblokkeerd" : "Blokkeren"}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="shrink-0"
              >
                <X size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column - Task Details */}
            <div className="space-y-4">
              {/* Description */}
              <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white">
                <div className="border-neutral-primary/10 border-b px-4 py-3">
                  <h3 className="text-neutral-primary text-sm font-semibold">
                    Omschrijving
                  </h3>
                </div>
                <div className="p-4">
                  {isEditingDescription ? (
                    <div className="space-y-2">
                      <textarea
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        className="min-h-[120px] w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleUpdateDescription}>
                          Opslaan
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setIsEditingDescription(false);
                            setEditedDescription(task.description || "");
                          }}
                        >
                          Annuleren
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="group -ml-2 min-h-[60px] cursor-pointer rounded-md px-2 py-2 hover:bg-neutral-50"
                      onClick={() => setIsEditingDescription(true)}
                    >
                      {task.description ? (
                        <p className="text-sm whitespace-pre-wrap text-neutral-600">
                          {task.description}
                        </p>
                      ) : (
                        <p className="text-sm text-neutral-400 italic">
                          Klik om een omschrijving toe te voegen
                        </p>
                      )}
                      <PencilSimple
                        size={14}
                        className="text-moss-primary mt-2 opacity-0 transition-opacity group-hover:opacity-100"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white">
                <div className="border-neutral-primary/10 border-b px-4 py-3">
                  <h3 className="text-neutral-primary text-sm font-semibold">
                    Toegewezen aan
                  </h3>
                </div>
                <div className="p-4">
                  {task.assignedTo ? (
                    <div
                      className="group -ml-2 flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-neutral-50"
                      onClick={() => setIsAssigneePickerOpen(true)}
                    >
                      <img
                        src={getUserImageUrl(task.assignedTo)}
                        alt={getUserName(task.assignedTo)}
                        className="border-moss-primary h-10 w-10 rounded-full border-2"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getUserName(task.assignedTo || ""))}&background=31403B&color=F9F0EE`;
                        }}
                      />
                      <div className="flex-1">
                        <div className="text-neutral-primary text-sm font-medium">
                          {getUserName(task.assignedTo)}
                        </div>
                        <div className="text-moss-primary flex items-center gap-1 text-xs">
                          <UserCircle size={12} weight="fill" />
                          Toegewezen
                        </div>
                      </div>
                      <PencilSimple
                        size={14}
                        className="text-moss-primary opacity-0 transition-opacity group-hover:opacity-100"
                        weight="bold"
                      />
                    </div>
                  ) : (
                    <div
                      className="-ml-2 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 transition-colors hover:bg-neutral-50"
                      onClick={() => setIsAssigneePickerOpen(true)}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-200">
                        <UserCircle size={20} className="text-neutral-500" />
                      </div>
                      <p className="flex-1 text-sm text-neutral-500">
                        Niet toegewezen
                      </p>
                      <PencilSimple size={14} className="text-neutral-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Project Phase */}
              <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white">
                <div className="border-neutral-primary/10 border-b px-4 py-3">
                  <h3 className="text-neutral-primary text-sm font-semibold">
                    Projectfase
                  </h3>
                </div>
                <div className="p-4">
                  {isEditingPhase ? (
                    <div className="space-y-2">
                      <select
                        value={task.projectPhase || "NONE"}
                        onChange={(e) => handleUpdatePhase(e.target.value)}
                        className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                        autoFocus
                      >
                        <option value="NONE">Geen fase</option>
                        <option value="Ontwerp fase">Ontwerp</option>
                        <option value="Gepland">Uitvoeringsdossier</option>
                        <option value="In uitvoering">
                          In uitvoering - Werf
                        </option>
                        <option value="Afgerond">Voorlopige oplevering</option>
                        <option value="Volledig gefactureerd">
                          Definitieve oplevering
                        </option>
                      </select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsEditingPhase(false)}
                      >
                        Annuleren
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="group -ml-2 cursor-pointer rounded-md px-2 py-2 hover:bg-neutral-50"
                      onClick={() => setIsEditingPhase(true)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-neutral-700">
                          {task.projectPhase ? (
                            task.projectPhase === "Ontwerp fase" ? (
                              "Ontwerp"
                            ) : task.projectPhase === "Gepland" ? (
                              "Uitvoeringsdossier"
                            ) : task.projectPhase === "In uitvoering" ? (
                              "In uitvoering - Werf"
                            ) : task.projectPhase === "Afgerond" ? (
                              "Voorlopige oplevering"
                            ) : task.projectPhase ===
                              "Volledig gefactureerd" ? (
                              "Definitieve oplevering"
                            ) : (
                              task.projectPhase
                            )
                          ) : (
                            <span className="text-neutral-400 italic">
                              Geen fase
                            </span>
                          )}
                        </span>
                        <PencilSimple
                          size={14}
                          className="text-moss-primary opacity-0 transition-opacity group-hover:opacity-100"
                          weight="bold"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Todo List */}
            <div>
              <TodoListSection
                entityType="project-task"
                entityId={task.id}
                title="Todo Lijst"
              />
            </div>
          </div>

          {/* Action Notes Section - Full Width Below */}
          <div className="mt-6">
            <div className="border-neutral-primary/20 overflow-hidden rounded-lg border bg-white">
              <ProjectTaskActionNotesSection task={task} />
            </div>
          </div>
        </div>

        {/* Assignee Picker Modal */}
        <UniversalPickerModal
          isOpen={isAssigneePickerOpen}
          onClose={() => setIsAssigneePickerOpen(false)}
          mode="assignee"
          title="Wijs toe aan"
          onConfirm={(user) => {
            handleUpdateAssignee(user);
            setIsAssigneePickerOpen(false);
          }}
          config={{
            defaultValue: task.assignedTo,
            allowRemoval: true,
          }}
        />

        {/* Block Task Modal */}
        <BlockTaskModal
          isOpen={isBlockModalOpen}
          onClose={() => setIsBlockModalOpen(false)}
          task={task}
          onSuccess={() => {
            setIsBlockModalOpen(false);
            onUpdate();
          }}
          onOptimisticUpdate={(updates) => {
            // Update local task state immediately
            setTask((prevTask) =>
              prevTask ? { ...prevTask, ...updates } : prevTask,
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
